// SparkleField.jsx
// Ambient drifting sparkle/star decoration. Pointer-events: none. Used as a
// background layer in StartingSoon, Break, Outro to add warmth and motion
// without competing with content.
//
// Uses grid-jittered placement instead of pure random — divides the viewport
// into a grid and places one sparkle per cell with random jitter within the
// cell. This prevents clustering and ensures even coverage.
import { useMemo } from "react";
import useObsActive from "../../hooks/useObsActive";

const SPARKLE_KEY = "jopSparkleDrift";

const STAR_GLYPHS = ["✦", "✧", "★", "✨", "·"];
const COLORS = [
  "var(--jop-gold)",
  "var(--jop-highlight-blue)",
  "var(--jop-pink)",
  "var(--jop-cream)",
];

function rand(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

export default function SparkleField({ density = 30, seed = 1 }) {
  const active = useObsActive();
  const sparkles = useMemo(() => {
    const r = rand(seed);
    // Calculate grid dimensions to approximate the desired density
    const cols = Math.ceil(Math.sqrt(density * 1.8)); // wider than tall
    const rows = Math.ceil(density / cols);
    const cellW = 100 / cols;
    const cellH = 100 / rows;

    const result = [];
    for (let row = 0; row < rows && result.length < density; row++) {
      for (let col = 0; col < cols && result.length < density; col++) {
        // Place within the cell with jitter (60% of cell size, centered)
        const jitterX = (r() - 0.5) * cellW * 0.7;
        const jitterY = (r() - 0.5) * cellH * 0.7;
        result.push({
          glyph: STAR_GLYPHS[Math.floor(r() * STAR_GLYPHS.length)],
          color: COLORS[Math.floor(r() * COLORS.length)],
          left: (col + 0.5) * cellW + jitterX,
          top: (row + 0.5) * cellH + jitterY,
          size: 0.7 + r() * 1.4,
          delay: r() * 6,
          duration: 4 + r() * 4,
        });
      }
    }
    return result;
  }, [density, seed]);

  return (
    <>
      <style>{`
        @keyframes ${SPARKLE_KEY} {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.35; }
          50%      { transform: translateY(-10px) scale(1.2); opacity: 0.9; }
        }
      `}</style>
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden",
      }}>
        {sparkles.map((s, i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              left: `${Math.max(1, Math.min(99, s.left))}%`,
              top: `${Math.max(1, Math.min(99, s.top))}%`,
              fontSize: `${s.size}rem`,
              color: s.color,
              // text-shadow (NOT filter: drop-shadow) — the glow is baked into
              // the glyph's paint texture once, so the transform/opacity drift
              // composites for free. An animated drop-shadow FILTER re-rasters
              // every frame on the GPU compositor thread (the confirmed heat
              // source from the perf investigation) — this looks identical.
              textShadow: "0 0 6px rgba(255,255,255,0.4)",
              animation: `${SPARKLE_KEY} ${s.duration}s ease-in-out ${s.delay}s infinite`,
              // Freeze (don't tear down) when this overlay is off-air in OBS.
              animationPlayState: active ? "running" : "paused",
              userSelect: "none",
            }}
          >{s.glyph}</span>
        ))}
      </div>
    </>
  );
}
